import { atom, useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { BookmarksState, UseBookmarksReturn } from "./type";
import * as socialAPI from "../../Data/Social";
import { postsState } from "./Posts";
// Note: Import types as needed for function parameters

// Bookmarks state atom
export const bookmarksState = atom<BookmarksState>({
    key: "bookmarksState" + randomString(4),
    default: {
        items: {},
        loading: false,
        error: null
    }
});

export const useBookmarks = (): UseBookmarksReturn => {
    const [state, setState] = useRecoilState(bookmarksState);
    const resetBookmarks = useResetRecoilState(bookmarksState);
    const setPostsState = useSetRecoilState(postsState);

    // Internal function to update metrics from external sources (like Posts atom)
    const updatePostBookmarkFromExternal = useCallback((postid: string, count: number, userBookmarked: boolean): void => {
        setState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: { count, userBookmarked }
            }
        }));
    }, [setState]);

    // Update bookmark metrics for a specific post
    const updatePostBookmark = useCallback((postid: string, count: number, userBookmarked: boolean): void => {
        setState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: { count, userBookmarked }
            }
        }));
    }, [setState]);

    // Bookmark a post
    const bookmarkPost = useCallback(async (postid: string, folderName: string = 'default'): Promise<boolean> => {
        // Optimistic update - bookmarks state
        setState(prev => {
            const currentData = prev.items[postid] || { count: 0, userBookmarked: false };
            return {
                ...prev,
                items: {
                    ...prev.items,
                    [postid]: {
                        count: currentData.count + 1,
                        userBookmarked: true
                    }
                }
            };
        });

        // Optimistic update - posts state bookmark_count
        setPostsState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: prev.items[postid] ? {
                    ...prev.items[postid],
                    bookmark_count: prev.items[postid].bookmark_count + 1
                } : prev.items[postid]
            }
        }));

        try {
            const result = await socialAPI.bookmarkPost({ postid, folder_name: folderName });
            
            if (result) {
                return true;
            } else {
                // Revert optimistic update on failure - bookmarks state
                setState(prev => {
                    const currentData = prev.items[postid] || { count: 1, userBookmarked: true };
                    return {
                        ...prev,
                        items: {
                            ...prev.items,
                            [postid]: {
                                count: Math.max(0, currentData.count - 1),
                                userBookmarked: false
                            }
                        }
                    };
                });
                
                // Revert optimistic update on failure - posts state
                setPostsState(prev => ({
                    ...prev,
                    items: {
                        ...prev.items,
                        [postid]: prev.items[postid] ? {
                            ...prev.items[postid],
                            bookmark_count: Math.max(0, prev.items[postid].bookmark_count - 1)
                        } : prev.items[postid]
                    }
                }));
                
                return false;
            }
        } catch (error) {
            // Revert optimistic update on error - bookmarks state
            setState(prev => {
                const currentData = prev.items[postid] || { count: 1, userBookmarked: true };
                return {
                    ...prev,
                    items: {
                        ...prev.items,
                        [postid]: {
                            count: Math.max(0, currentData.count - 1),
                            userBookmarked: false
                        }
                    },
                    error: error instanceof Error ? error.message : "Failed to bookmark post"
                };
            });
            
            // Revert optimistic update on error - posts state
            setPostsState(prev => ({
                ...prev,
                items: {
                    ...prev.items,
                    [postid]: prev.items[postid] ? {
                        ...prev.items[postid],
                        bookmark_count: Math.max(0, prev.items[postid].bookmark_count - 1)
                    } : prev.items[postid]
                }
            }));
            
            console.error('Failed to bookmark post:', error);
            return false;
        }
    }, [setState, setPostsState]);

    // Remove bookmark from a post
    const unbookmarkPost = useCallback(async (postid: string): Promise<boolean> => {
        // Optimistic update - bookmarks state
        setState(prev => {
            const currentData = prev.items[postid] || { count: 1, userBookmarked: true };
            return {
                ...prev,
                items: {
                    ...prev.items,
                    [postid]: {
                        count: Math.max(0, currentData.count - 1),
                        userBookmarked: false
                    }
                }
            };
        });

        // Optimistic update - posts state bookmark_count
        setPostsState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: prev.items[postid] ? {
                    ...prev.items[postid],
                    bookmark_count: Math.max(0, prev.items[postid].bookmark_count - 1)
                } : prev.items[postid]
            }
        }));

        try {
            const result = await socialAPI.removeBookmark({ postid });
            
            if (result) {
                return true;
            } else {
                // Revert optimistic update on failure - bookmarks state
                setState(prev => {
                    const currentData = prev.items[postid] || { count: 0, userBookmarked: false };
                    return {
                        ...prev,
                        items: {
                            ...prev.items,
                            [postid]: {
                                count: currentData.count + 1,
                                userBookmarked: true
                            }
                        }
                    };
                });
                
                // Revert optimistic update on failure - posts state
                setPostsState(prev => ({
                    ...prev,
                    items: {
                        ...prev.items,
                        [postid]: prev.items[postid] ? {
                            ...prev.items[postid],
                            bookmark_count: prev.items[postid].bookmark_count + 1
                        } : prev.items[postid]
                    }
                }));
                
                return false;
            }
        } catch (error) {
            // Revert optimistic update on error - bookmarks state
            setState(prev => {
                const currentData = prev.items[postid] || { count: 0, userBookmarked: false };
                return {
                    ...prev,
                    items: {
                        ...prev.items,
                        [postid]: {
                            count: currentData.count + 1,
                            userBookmarked: true
                        }
                    },
                    error: error instanceof Error ? error.message : "Failed to remove bookmark"
                };
            });
            
            // Revert optimistic update on error - posts state
            setPostsState(prev => ({
                ...prev,
                items: {
                    ...prev.items,
                    [postid]: prev.items[postid] ? {
                        ...prev.items[postid],
                        bookmark_count: prev.items[postid].bookmark_count + 1
                    } : prev.items[postid]
                }
            }));
            
            console.error('Failed to remove bookmark:', error);
            return false;
        }
    }, [setState, setPostsState]);

    // Sync user bookmarks from API
    const syncUserBookmarks = useCallback(async (): Promise<void> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            // Fetch user's bookmarks from API
            const apiBookmarks = await socialAPI.listAllBookmarks();
            
            if (apiBookmarks) {
                // Update state with bookmark data
                const bookmarkItems: Record<string, { count: number, userBookmarked: boolean }> = {};
                
                apiBookmarks.forEach(bookmark => {
                    bookmarkItems[bookmark.postid] = {
                        count: 0, // Bookmarks don't have counts, just user status
                        userBookmarked: true
                    };
                });
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, ...bookmarkItems },
                    loading: false
                }));
            } else {
                setState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : "Failed to sync user bookmarks",
                loading: false
            }));
            console.error('Failed to sync user bookmarks:', error);
        }
    }, [setState]);

    return {
        postBookmarks: state.items,
        loading: state.loading,
        error: state.error,
        
        // Bookmark functions
        updatePostBookmark,
        updatePostBookmarkFromExternal,
        bookmarkPost,
        unbookmarkPost,
        syncUserBookmarks,

        reset: resetBookmarks
    };
};